// THE WHEEL — fifty segments, one pointer, up to ten spins on a ticket.
//
// Nothing to choose but how much and how many. The stake splits evenly across the spins and each
// spin pays its own segment, so a ten-spin ticket is ten small bets settled by one beacon round.
//
// The wheel is drawn from WHEEL_LAYOUT, the same fifty codes the contract carries, and it stops on
// segmentOf() read back from the cabinet — one spin at a time, with a beat between them, so a
// ten-spin ticket plays out as ten spins rather than one sum appearing.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import { CHIP, revealWheel } from 'lib/casino';
import { WHEEL_LAYOUT, WHEEL_MAX_SPINS, WHEEL_SEGMENTS, wheelMultBps } from 'lib/casino-math';
import sfx from 'lib/sfx';
import { AutoPanel, useAuto } from './auto';
import { useBet, useStakeCap } from './state';
import { FONT, HouseRules, Outcome, Panel, PlayButton, StakeBox, T, fmtChip, parseUnits, xpButton } from './ui';

const BPS = BigInt(10000);
const ALL_IN_BPS = BigInt(10500);
const STEP = 360 / WHEEL_SEGMENTS;
const LAND_SECONDS = 2.4; // one spin's deceleration
const BETWEEN_MS = 650; // the beat between spins on a multi-spin ticket
const SPIN_SECONDS = 12; // the free-spin leg while the round is still in the air

const PRIZE = [
  { label: '0x', fill: '#0a3d22', ink: '#5f8f74', star: false },
  { label: '1.5x', fill: '#ffd86b', ink: '#4a3600', star: false },
  { label: '2x', fill: '#ff8c1a', ink: '#3b1d00', star: false },
  { label: '3x', fill: '#c8102e', ink: '#fff0f0', star: false },
  { label: '6x', fill: '#7b2ff7', ink: '#fff', star: true },
];

const R_OUT = 106;
const R_IN = 62;
const CX = 120;
const CY = 120;

function polar(r, deg) {
  const rad = ((deg - 90) * Math.PI) / 180;
  return [CX + r * Math.cos(rad), CY + r * Math.sin(rad)];
}

const SEGMENTS = WHEEL_LAYOUT.map((code, i) => {
  const mid = i * STEP;
  const a1 = mid - STEP / 2;
  const a2 = mid + STEP / 2;
  const [ox1, oy1] = polar(R_OUT, a1);
  const [ox2, oy2] = polar(R_OUT, a2);
  const [ix2, iy2] = polar(R_IN, a2);
  const [ix1, iy1] = polar(R_IN, a1);
  const [lx, ly] = polar(R_OUT - 15, mid);
  return {
    code,
    index: i,
    d: `M${ox1.toFixed(2)} ${oy1.toFixed(2)}A${R_OUT} ${R_OUT} 0 0 1 ${ox2.toFixed(2)} ${oy2.toFixed(2)}L${ix2.toFixed(2)} ${iy2.toFixed(2)}A${R_IN} ${R_IN} 0 0 0 ${ix1.toFixed(2)} ${iy1.toFixed(2)}Z`,
    lx,
    ly,
    mid,
  };
});

/** How many of each prize the fifty segments carry — the board, printed rather than claimed. */
const COUNTS = PRIZE.map((_, code) => WHEEL_LAYOUT.filter(c => c === code).length);

export default function Wheel({ casino }) {
  const [amount, setAmount] = useState('5');
  const [spins, setSpins] = useState(3);
  const [segments, setSegments] = useState(null);
  const [shown, setShown] = useState(0);
  const [deg, setDeg] = useState(0);
  const [glide, setGlide] = useState({ dur: 0, ease: 'linear' });
  const bet = useBet('wheel', casino);

  const locked = bet.phase !== 'idle' && bet.phase !== 'done';
  const pending = bet.phase === 'approving' || bet.phase === 'placing' || bet.phase === 'waiting' || bet.phase === 'settling';

  const allIn = useMemo(() => parseUnits(amount, CHIP.decimals), [amount]);
  const stake = (allIn * BPS) / ALL_IN_BPS;
  const perSpin = stake / BigInt(spins);
  const bestCase = (perSpin * BigInt(60000) * BigInt(spins)) / BPS;

  // The house refuses a bet whose worst case it cannot cover, and the refusal is a revert with
  // no explanation. Read the live ceiling and never let the player submit past it.
  const stakeCap = useStakeCap('wheel', 60000, casino, amount, setAmount);
  const overCap = stakeCap !== null && stakeCap > BigInt(0) && allIn > stakeCap;
  const canPlay = casino.ready && allIn > BigInt(0) && perSpin > BigInt(0) && !overCap;
  const auto = useAuto(bet, { allIn, args: [spins], canPlay, balance: casino.balance });

  // The settled segments, read back from the cabinet's own view of the round.
  useEffect(() => {
    let alive = true;
    setSegments(null);
    setShown(0);
    if (!bet.result) return undefined;
    revealWheel(bet.result.id, spins, bet.result.randomness)
      .then(s => {
        if (alive) setSegments(s.map(Number));
      })
      .catch(() => {
        // The receipt is the authority; an unreadable view leaves the wheel still rather than wrong.
        if (alive) setSegments(null);
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bet.result]);

  // Leg one: free-spin while the round is in the air.
  useEffect(() => {
    if (!pending) return;
    setGlide({ dur: SPIN_SECONDS, ease: 'linear' });
    setDeg(d => d + 360 * SPIN_SECONDS);
    if (sfx.wheelSpin) sfx.wheelSpin(SPIN_SECONDS * 1000, 70);
  }, [pending]);

  // Leg two: every spin of the ticket, in order, with a beat between them.
  useEffect(() => {
    if (!segments || segments.length === 0) return undefined;
    const run = { cancelled: false, timer: null };
    let i = 0;

    const settleOne = () => {
      if (run.cancelled) return;
      const index = segments[i];
      setGlide({ dur: LAND_SECONDS, ease: 'cubic-bezier(0.15, 0.68, 0.12, 1)' });
      setDeg(current => {
        const base = -index * STEP;
        const turns = Math.ceil((current + 360 * 2 - base) / 360);
        return base + 360 * turns;
      });
      if (sfx.wheelSpin) sfx.wheelSpin(LAND_SECONDS * 1000, 26);
      run.timer = setTimeout(() => {
        if (run.cancelled) return;
        i += 1;
        setShown(i);
        if (sfx.ballDrop) sfx.ballDrop();
        if (i < segments.length) run.timer = setTimeout(settleOne, BETWEEN_MS);
        else run.timer = null;
      }, LAND_SECONDS * 1000);
    };

    settleOne();
    return () => {
      run.cancelled = true;
      if (run.timer !== null) {
        clearTimeout(run.timer);
        run.timer = null;
      }
    };
  }, [segments]);

  const landed = segments ? segments.slice(0, shown) : [];
  const tally = landed.reduce((sum, index) => sum + (perSpin * wheelMultBps(index)) / BPS, BigInt(0));
  const current = segments && shown > 0 ? WHEEL_LAYOUT[segments[shown - 1]] : null;

  return (
    <Div>
      <div className="wh__stage">
        <div className="wh__wheelwrap">
          <div className="wh__pointer" aria-hidden="true" />
          <svg className="wh__wheel" viewBox="0 0 240 240" role="img" aria-label="The wheel">
            <defs>
              <radialGradient id="wh-rim" cx="38%" cy="28%">
                <stop offset="0%" stopColor="#fff3c4" />
                <stop offset="45%" stopColor="#ffd86b" />
                <stop offset="100%" stopColor="#8a6a10" />
              </radialGradient>
            </defs>
            <circle cx={CX} cy={CY} r="118" fill="#2a1a06" />
            <circle cx={CX} cy={CY} r="114" fill="url(#wh-rim)" />
            <circle cx={CX} cy={CY} r={R_OUT + 2} fill="#3b2a00" />
            <g
              className="wh__rotor"
              style={{ transform: `rotate(${deg}deg)`, transitionDuration: `${glide.dur}s`, transitionTimingFunction: glide.ease }}
            >
              {SEGMENTS.map(s => (
                <g key={s.index}>
                  <path d={s.d} fill={PRIZE[s.code].fill} stroke="#6b5410" strokeWidth="0.5" />
                  {s.code === 0 ? null : (
                    <text
                      x={s.lx}
                      y={s.ly}
                      className="wh__label"
                      fill={PRIZE[s.code].ink}
                      transform={`rotate(${s.mid + 90} ${s.lx.toFixed(2)} ${s.ly.toFixed(2)})`}
                    >
                      {PRIZE[s.code].star ? '★' : PRIZE[s.code].label}
                    </text>
                  )}
                </g>
              ))}
              <circle cx={CX} cy={CY} r={R_IN} fill="#0a3d22" stroke="#c9a227" strokeWidth="2" />
              <circle cx={CX} cy={CY} r="30" fill="#081f12" stroke="#c9a227" strokeWidth="1" />
              <text x={CX} y={CY + 6} className="wh__hub">
                SPIN
              </text>
            </g>
          </svg>
        </div>

        <div className="wh__readout">
          <div className="wh__title">THE WHEEL</div>
          <div className={`wh__prize wh__prize--${current === null ? 'idle' : current}`}>
            {current === null ? '—' : PRIZE[current].label}
          </div>
          <div className="wh__spins">
            {Array.from({ length: spins }).map((_, i) => {
              const code = segments && i < shown ? WHEEL_LAYOUT[segments[i]] : null;
              return (
                <span key={i} className={`wh__pip${code === null ? '' : ` on p${code}`}`}>
                  {code === null ? i + 1 : PRIZE[code].label}
                </span>
              );
            })}
          </div>
          <div className="wh__tally">
            <span className="k">PAID SO FAR</span>
            <span className="v">
              {fmtChip(tally)} {CHIP.symbol}
            </span>
          </div>
          <div className="wh__caption">
            {segments === null
              ? pending
                ? 'the wheel is turning'
                : `${spins} spin${spins === 1 ? '' : 's'} on this ticket`
              : `spin ${Math.max(shown, 1)} of ${spins}`}
          </div>
        </div>
      </div>

      <Panel>
        <div className="wh__bar">
          <span className="wh__barlabel">SPINS</span>
          {Array.from({ length: WHEEL_MAX_SPINS }).map((_, i) => (
            <button
              key={i + 1}
              type="button"
              className={`wh__pick${i + 1 === spins ? ' on' : ''}`}
              disabled={locked}
              onClick={() => setSpins(i + 1)}
            >
              {i + 1}
            </button>
          ))}
        </div>

        <div className="wh__board">
          {PRIZE.map((p, code) => (
            <div key={p.label} className="wh__boardrow">
              <i style={{ background: p.fill }} />
              <b>{p.label}</b>
              <span>
                {COUNTS[code]} of {WHEEL_SEGMENTS}
              </span>
            </div>
          ))}
        </div>

        <div className="wh__quote">
          <div>
            <span className="k">Pays</span>
            <span className="v">up to 6.00x</span>
          </div>
          <div>
            <span className="k">Per spin</span>
            <span className="v">
              {fmtChip(perSpin)} {CHIP.symbol}
            </span>
          </div>
          <div>
            <span className="k">Best case</span>
            <span className="v">
              {fmtChip(bestCase)} {CHIP.symbol}
            </span>
          </div>
        </div>

        <StakeBox value={amount} onChange={setAmount} disabled={locked} />

        <PlayButton
          label="SPIN"
          onClick={() => bet.play(allIn, [spins])}
          disabled={!canPlay}
          phase={bet.phase}
          note={bet.note}
          countdown={bet.countdown}
        />

        <AutoPanel auto={auto} practice={casino.practice} />

        {bet.phase === 'stuck' ? (
          <div className="wh__warn">
            Your bet is placed and still open. <button onClick={bet.retrySettle}>Settle again</button>
          </div>
        ) : null}

        <Outcome result={bet.result} onAgain={bet.reset}>
          {segments === null ? null : (
            <div className="wh__receipt">
              {segments.map(index => PRIZE[WHEEL_LAYOUT[index]].label).join(' · ')}
            </div>
          )}
        </Outcome>

        <HouseRules
          practice={casino.practice}
        />
      </Panel>
    </Div>
  );
}

const Div = styled.div`
  .wh__stage {
    display: flex;
    gap: 14px;
    align-items: center;
    flex-wrap: wrap;
    justify-content: center;
    background: radial-gradient(ellipse at 50% 28%, #1a8a4c, ${T.felt} 55%, ${T.feltDark} 100%);
    border: 4px solid #5a3a12;
    border-radius: 10px;
    box-shadow: inset 0 0 0 2px ${T.goldDeep}, inset 0 0 40px rgba(0, 0, 0, 0.55), 0 4px 8px rgba(0, 0, 0, 0.5);
    padding: 14px 16px;
  }
  .wh__wheelwrap {
    position: relative;
    width: 232px;
    height: 232px;
    flex-shrink: 0;
  }
  .wh__wheel {
    width: 100%;
    height: 100%;
    display: block;
    filter: drop-shadow(0 6px 10px rgba(0, 0, 0, 0.55));
  }
  .wh__rotor {
    transform-origin: 120px 120px;
    transition-property: transform;
  }
  .wh__label {
    font: bold 7px ${FONT.web};
    text-anchor: middle;
    dominant-baseline: middle;
  }
  .wh__hub {
    font: bold 13px ${FONT.serif};
    fill: ${T.gold};
    text-anchor: middle;
    letter-spacing: 3px;
  }
  .wh__pointer {
    position: absolute;
    top: -2px;
    left: 50%;
    width: 0;
    height: 0;
    margin-left: -9px;
    border-left: 9px solid transparent;
    border-right: 9px solid transparent;
    border-top: 20px solid ${T.gold};
    filter: drop-shadow(0 2px 2px rgba(0, 0, 0, 0.6));
    z-index: 2;
  }
  @media (prefers-reduced-motion: reduce) {
    .wh__rotor {
      transition-duration: 0s !important;
    }
  }

  .wh__readout {
    flex: 1;
    min-width: 168px;
    text-align: center;
  }
  .wh__title {
    font: bold 10px ${FONT.serif};
    letter-spacing: 5px;
    color: ${T.gold};
  }
  .wh__prize {
    margin: 6px auto 0;
    padding: 8px 0;
    border: 3px double ${T.goldDeep};
    font: bold 30px ${FONT.serif};
    color: #fffbe6;
    background: #081f12;
  }
  .wh__prize--1 {
    background: #ffd86b;
    color: #4a3600;
  }
  .wh__prize--2 {
    background: #ff8c1a;
    color: #3b1d00;
  }
  .wh__prize--3 {
    background: #c8102e;
  }
  .wh__prize--4 {
    background: #7b2ff7;
    text-shadow: 0 0 12px #d9b3ff;
  }
  .wh__spins {
    display: flex;
    flex-wrap: wrap;
    gap: 3px;
    justify-content: center;
    margin-top: 8px;
  }
  .wh__pip {
    min-width: 26px;
    padding: 2px 3px;
    font: bold 9px ${FONT.web};
    color: #b8d9c4;
    background: rgba(0, 0, 0, 0.3);
    border: 1px solid rgba(255, 216, 107, 0.4);
  }
  .wh__pip.on {
    color: #fff;
  }
  .wh__pip.p0 {
    background: #0a3d22;
  }
  .wh__pip.p1 {
    background: #ffd86b;
    color: #4a3600;
  }
  .wh__pip.p2 {
    background: #ff8c1a;
    color: #3b1d00;
  }
  .wh__pip.p3 {
    background: #c8102e;
  }
  .wh__pip.p4 {
    background: #7b2ff7;
  }
  .wh__tally {
    margin-top: 9px;
    background: #000;
    border: 2px inset #808080;
    padding: 4px 6px;
  }
  .wh__tally .k {
    display: block;
    font: 8px ${FONT.web};
    letter-spacing: 2px;
    color: #d9c27a;
  }
  .wh__tally .v {
    display: block;
    font: bold 15px ${FONT.mono};
    color: ${T.gold};
    text-shadow: 0 0 8px rgba(255, 216, 107, 0.6);
  }
  .wh__caption {
    margin-top: 7px;
    font: 10px ${FONT.web};
    color: ${T.cream};
  }

  .wh__bar {
    display: flex;
    align-items: center;
    gap: 4px;
    margin-top: 12px;
    flex-wrap: wrap;
  }
  .wh__barlabel {
    font: 11px ${FONT.ui};
    color: #000;
    margin-right: 3px;
  }
  .wh__pick {
    ${xpButton};
    min-width: 28px;
    min-height: 21px;
    padding: 0 5px;
  }
  .wh__pick.on {
    background: linear-gradient(180deg, #fff3c4 0%, #ffd86b 45%, #d8a72a 100%);
    border-color: #8a6a10;
    font-weight: 700;
  }

  .wh__board {
    display: flex;
    gap: 5px;
    margin-top: 10px;
    flex-wrap: wrap;
  }
  .wh__boardrow {
    flex: 1;
    min-width: 78px;
    display: flex;
    align-items: center;
    gap: 5px;
    background: #fff;
    border: 1px solid #7f9db9;
    box-shadow: inset 1px 1px 0 #d6dde5;
    padding: 3px 5px;
    font: 10px ${FONT.ui};
  }
  .wh__boardrow i {
    width: 10px;
    height: 10px;
    border: 1px solid #555;
    flex-shrink: 0;
  }
  .wh__boardrow b {
    color: #000;
  }
  .wh__boardrow span {
    color: #555;
    font-size: 9px;
    margin-left: auto;
  }

  .wh__quote {
    display: flex;
    gap: 6px;
    margin-top: 10px;
    flex-wrap: wrap;
  }
  .wh__quote > div {
    flex: 1;
    min-width: 92px;
    background: #fff;
    border: 1px solid #7f9db9;
    box-shadow: inset 1px 1px 0 #d6dde5;
    padding: 3px 6px;
  }
  .wh__quote .k {
    display: block;
    font-size: 9px;
    color: #555;
  }
  .wh__quote .v {
    display: block;
    font-size: 12px;
    font-weight: 700;
    color: #000;
    margin-top: 1px;
  }
  .wh__warn {
    margin-top: 8px;
    font: 10px ${FONT.ui};
    color: #8a0000;
  }
  .wh__warn button {
    ${xpButton};
    min-width: 0;
    min-height: 19px;
    padding: 0 8px;
    margin-left: 6px;
  }
  .wh__receipt {
    font: 11px ${FONT.mono};
    margin-bottom: 4px;
    letter-spacing: 1px;
  }
`;
